import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { EffectFade, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
} from '../styles';
import { Container, Slider } from './ui';

const StyledTestimonials = styled.section`
  ${sectionPaddings()};

  ${({ isDetail }) => {
    if (isDetail) {
      return css`
        ${sectionPaddings('30px', '60px')};
      `;
    }
  }}

  ${({ alt }) => {
    if (alt) {
      return css`
        background-color: ${brandColours.primary};
      `;
    } else {
      return css`
        background-color: ${standardColours.lightGrey};
      `;
    }
  }}
`;

const StyledSlider = styled(Slider)`
  .swiper-pagination {
    position: relative;
    margin-top: 40px;
    ${minBreakpointQuery.small`
      margin-top: 50px;
    `}

    ${minBreakpointQuery.smedium`
      margin-top: 60px;
    `}

    ${minBreakpointQuery.medium`
      margin-top: 70px;
    `}

    ${minBreakpointQuery.large`
      margin-top: 80px;
    `}
  }
  .swiper-pagination-bullet {
    width: 10px;
    height: 10px;
    opacity: 1;
  }
  .swiper-slide {
    height: auto;
    display: flex;
    align-self: center;
  }

  .swiper-pagination-horizontal.swiper-pagination-bullets
    .swiper-pagination-bullet {
    margin-left: 6px;
    margin-right: 6px;
  }

  .swiper-pagination-bullet {
    background-color: ${({ alt }) =>
      alt ? `${brandColours.quaternary}` : `${brandColours.denary}`};
  }

  .swiper-pagination-bullet-active {
    background-color: ${brandColours.secondary};
  }
`;

const StyledSlide = styled.blockquote`
  display: flex;
  align-items: center;
  flex-direction: column;

  ${({ isDetail }) => {
    if (isDetail) {
      return css`
        gap: 20px;
        color: ${brandColours.quaternary};
        ${minBreakpointQuery.small`
          gap: 30px;
        `}

        ${minBreakpointQuery.large`
          gap: 40px;
        `}
      `;
    } else {
      return css`
        gap: 60px;
        color: ${brandColours.primary};
      `;
    }
  }}

  ${({ alt }) => {
    if (alt) {
      return css`
        color: ${standardColours.white};
      `;
    }
  }}
`;

const StyledQuote = styled.p`
  text-align: center;
  &:before {
    content: '“';
    display: block;
    font-family: Arial;
    color: ${brandColours.secondary};
    height: 84px;
    line-height: 1;

    ${({ isDetail }) => fontSize(isDetail ? 140 : 200)};
  }

  ${({ isDetail }) => {
    if (isDetail) {
      return css`
        ${fluidFontSize(
          '18px',
          '24px',
          breakpointSizes.tiny,
          breakpointSizes.xxxxlarge,
        )};
        line-height: 1.6;
      `;
    } else {
      return css`
        ${fluidFontSize(
          '24px',
          '36px',
          breakpointSizes.tiny,
          breakpointSizes.xxxxlarge,
        )};
        line-height: 1.5;
      `;
    }
  }}
`;

const StyledAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;

  ${maxBreakpointQuery.small`
    flex-direction: column;
  `}
`;

const StyledImage = styled(GatsbyImage)`
  height: 86px;
  width: 86px;
  border-radius: 50%;
`;

const StyledTextWrapper = styled.div`
  ${({ isDetail }) => {
    if (!isDetail) {
      return css`
        ${minBreakpointQuery.small`
          border-left: 1px solid ;
          padding-left: 25px;
        `}
      `;
    }
  }}
  border-color:${({ alt }) =>
    alt ? `${standardColours.white}` : `${brandColours.primary}`};
`;

const StyledNameRole = styled.p`
  ${fontSize(14)};
`;

const StyledOrganisation = styled.p`
  ${fontSize(12)};
  margin-top: 10px;
`;

export const Testimonials = ({ alt, items, isDetail }) => {
  const sliderOptions = {
    modules: [EffectFade, Pagination, Autoplay],
    fadeEffect: { crossFade: true },
    autoHeight: true,
    autoplay: {
      delay: 7000,
      pauseOnMouseEnter: true,
      disableOnInteraction: false,
    },
    slidesPerView: 1,
    effect: 'fade',
    pagination: { clickable: true },
  };

  return (
    <StyledTestimonials alt={alt} isDetail={isDetail}>
      <Container narrow={true}>
        <StyledSlider alt={alt} {...sliderOptions}>
          {items.map(({ name, role, organisation, image, quote }, id) => (
            <StyledSlide key={id} alt={alt} isDetail={isDetail}>
              <StyledQuote isDetail={isDetail}>{quote}</StyledQuote>
              <StyledAuthor alt={alt}>
                {image && (
                  <StyledImage image={image.gatsbyImageData} alt={image.alt} />
                )}
                <StyledTextWrapper alt={alt} isDetail={isDetail}>
                  <StyledNameRole>{`${name}, ${role}`}</StyledNameRole>
                  <StyledOrganisation>{organisation}</StyledOrganisation>
                </StyledTextWrapper>
              </StyledAuthor>
            </StyledSlide>
          ))}
        </StyledSlider>
      </Container>
    </StyledTestimonials>
  );
};
export default Testimonials;
